<template>
  <div>
    <load-progress :condition="!dataLoaded" label="Chargement des données"></load-progress>
    <div v-if="dataLoaded">
      <section-header :ressource="ressource"
                      :isExtended="true"
                      :dialog="true"
                      @closeDialog=closeDialog
                      ref="WIZARD">
      </section-header>
      <div class='py-2'>


        <edit-field :value="wizard.label"
                    :editFieldInfo="labelFieldInfo"
                    @updated="setLabel">
        </edit-field>
        
        <select-field :editFieldInfo="moduleFieldInfo"
                      :value="getModuleValue()"
                      :items="modules"
                      :notOrdered="true"
                      @updated="setModule">
        </select-field>
        
        <v-radio-group v-model="profil" class="radio-group" row>
          <v-radio :label=salarieFieldInfo
                   :value="salarieValue">
          </v-radio>
          <v-radio :label=employeurFieldInfo
                   :value="employeurValue">
          </v-radio>
          <v-radio :label=agetaFieldInfo
                   :value="agetaValue">
          </v-radio>
        </v-radio-group>

        <check-field :value="getCi98()"
                      :editFieldInfo="ci98FieldInfo"
                      @updated="setCi98">
        </check-field>

        <check-field :value="getActive()"
                     :editFieldInfo="activeFieldInfo"
                     @updated="setActive">
        </check-field>

        <div>
          <section-header :ressource="ressourcePage"
                          :disableExtend="true"
                          :isExtended="true"
                          @callback="addWizardItem"
                          addLabel="Ajouter"
                          :canAdd="true"
                          ref="DESCRIPTION_ITEM">
          </section-header>
          <v-table class="pb-5" dense>
            <thead class="pb-1">
              <tr class="font-weight-bold">
                <td v-for="(header, index) in headers" :key="index" style="vertical-align: bottom; font-size: 14px; border-bottom: thin solid rgba(0, 0, 0, 0.12);">
                  {{ header }}
                </td>
                <td width="2%"></td>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in wizardItems" :key="index">

                <td class="edit-field text-left py-3" width="10%">
                  <v-row class="justify-start">
                    <div v-if="canMoveUp(index)" class="d-flex justify-center" @click.stop="moveUp(index)">
                      <v-icon size="23" color="#e42125">fas fa-chevron-up</v-icon>
                    </div>
                    <div v-if="canMoveDown(index)" class="d-flex justify-center" @click.stop="moveDown(index)">
                      <v-icon size="23" color="#e42125">fas fa-chevron-down</v-icon>
                    </div>
                  </v-row>
                </td>

                <td class="edit-field text-left">{{ item.label }}</td>

                <td>
                  <div class="d-flex">
                    <div class="d-flex justify-center" @click.stop="updateWizardItem(item)">
                      <v-icon color="#2a97da" size="23">fas fa-pencil</v-icon>
                    </div>
                    <div class="d-flex justify-center" @click.stop="deleteWizardItem(index)">
                      <v-icon color="#e42125" size="23">fas fa-trash-can</v-icon>
                    </div>
                  </div>
                </td>

              </tr>
            </tbody>
          </v-table>
        </div>
      </div>
      <div class="d-flex flex-row-reverse">

        <loader-button class="ms-2"
                        type='button'
                        :label="saveLabel"
                        :disabled="!updated"
                        @callback="save">
        </loader-button>
        <loader-button class="ms-2"
                        type='button'
                        :label="previewLabel"
                        :disabled="false"
                        @callback="preview">
        </loader-button>
      </div>

      <section-container :section="section"
                        :dialogOpen="dialogOpen"
                        :extended="true"
                        @closeDialog="closePreview">
      </section-container>
    </div>
  </div>
</template>

<script lang="ts">
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
import { FwkButtons, FwkControls, FwkSections, RessourceFwkButton, RessourceFwkControl, RessourceFwkSection } from '@/ressources/globalRessources';
import SectionHeader from '@/framework/base/components/SectionHeader.vue';
import EditField from '@/framework/base/components/EditField.vue';
import { FieldInfo, EditFieldType, EditFieldTheme } from '@/framework/base/components/FieldInfo';
import Wizard from '@/generated/classes/Wizard';
import Section from '@/framework/base/classes/Section';
import SectionWizard from '@/framework/wizard/components/SectionWizard.vue';
import SectionContainer from '@/framework/base/components/SectionContainer.vue';
import WizardItem from '@/generated/classes/WizardItem';
import SectionAdminWizardItem from '@/framework/wizard/components/SectionAdminWizardItem.vue';
import Module from '@/generated/classes/Module';
import Item from '@/framework/base/classes/Item';
import SelectField from '@/framework/base/components/SelectField.vue';
import CheckField from '@/framework/base/components/CheckField.vue';
import UserProfile, { EnumProfile } from '@/framework/base/classes/UserProfile';
import { storeWizardItem } from '@/generated/stores';
import LoadProgress from '@/framework/base/components/LoadProgress.vue';
import { ListFilter } from '@/generated/classes/ListData';
import { type PropType, defineComponent } from 'vue';

export default defineComponent({
  name: 'SectionAdminWizard',
  components: {
    LoaderButton,
    SectionHeader,
    EditField,
    SectionContainer,
    SelectField,
    CheckField,
    LoadProgress
  },
  props: {
    data: {
      type: Object as PropType<Wizard>,
      required: false,
    },
  },
  data() {
    return {
      wizard: new Wizard(),
      dialogOpen: false,
      updated: false,
      refWizard: new Wizard(),
      section: new Section(),
      modules: [] as Item[],
      salarieValue: EnumProfile.salarie,
      employeurValue: EnumProfile.client,
      agetaValue: EnumProfile.admin + EnumProfile.compta + EnumProfile.marketing + EnumProfile.support + EnumProfile.superAdmin,
    };
  },
  computed: {
    previewLabel(): any { return RessourceFwkButton(FwkButtons.VISUALISER).label; },
    saveLabel(): any { return RessourceFwkButton(FwkButtons.ENREGISTRER).label; },
    ressource(): any { return RessourceFwkSection(FwkSections.WIZARD); },
    ressourceLabel(): any { return RessourceFwkControl(FwkControls.LABEL).label; },
    ressourcePage(): any { return RessourceFwkSection(FwkSections.PAGE); },
    salarieFieldInfo(): any { return RessourceFwkControl(FwkControls.SALARIE).label; },
    employeurFieldInfo(): any { return RessourceFwkControl(FwkControls.EMPLOYEUR).label; },
    agetaFieldInfo(): any { return RessourceFwkControl(FwkControls.INTERNE).label; },
    headers(): any {
      return ["", this.ressourceLabel];
    },
    dataLoaded(): boolean {
      const res = storeWizardItem.wizardItemLoaded;
      return res;
    },
    wizardItems(): WizardItem[] {
      const res = this.wizard.wizardItems.sort((a, b) => a.position - b.position);
      return res;
    },
    labelFieldInfo(): FieldInfo {
      const ressource = RessourceFwkControl(FwkControls.LABEL);
      const type = EditFieldType.STRING;
      const precision = 2;
      const format = null;
      const mask = null;
      const focus = null;
      const live = null;
      return new FieldInfo(ressource, type, precision, mask, format, focus, live, EditFieldTheme.BASIC);
    },
    moduleFieldInfo(): FieldInfo {
      const ressource = RessourceFwkControl(FwkControls.MODULE);
      const type = EditFieldType.SELECT;
      const precision = 2;
      const format = null;
      const mask = null;
      const focus = null;
      const live = null;
      return new FieldInfo(ressource, type, precision, mask, format, focus, live, EditFieldTheme.BASIC);
    },
    ci98FieldInfo(): FieldInfo {
      const ressource = RessourceFwkControl(FwkControls.CI98);
      const type = EditFieldType.SELECT;
      const precision = 2;
      const format = null;
      const mask = null;
      const focus = null;
      const live = null;
      return new FieldInfo(ressource, type, precision, mask, format, focus, live, EditFieldTheme.BASIC);
    },
    activeFieldInfo(): FieldInfo {
      const ressource = RessourceFwkControl(FwkControls.ACTIVE);
      const type = EditFieldType.SELECT;
      const precision = 2;
      const format = null;
      const mask = null;
      const focus = null;
      const live = null;
      return new FieldInfo(ressource, type, precision, mask, format, focus, live, EditFieldTheme.BASIC);
    },
    profil: {
      get(): number {
        if ((this.wizard.profil & EnumProfile.salarie) > 0) {
          return EnumProfile.salarie;
        } else if ((this.wizard.profil & EnumProfile.client) > 0) {
          return EnumProfile.client;
        } else if (((this.wizard.profil & UserProfile.userInterne) > 0)) {
          return this.agetaValue;
        } else {
          return 0;
        }
      },
      set(value: number) {
        if (this.getCi98()) {
          this.wizard.profil = EnumProfile.ci98
        } else {
          this.wizard.profil = 0;
        }
        this.wizard.profil += value;
        this.setUpdated();
      },
    },
  },
  created(): void {
    const allData = new ListFilter();
    allData.pageLimit = -1;
    storeWizardItem.registerWizardItem(allData);
    Module.selectItems(false).then((res) => {
      res.sort((a, b) => a.label > b.label ? 1 : -1);
      res.forEach((module) => {
        const item = new Item(module.name, module.label);
        this.modules.push(item);
      })
    })
    if (this.data && this.data.id > 0) {
      this.wizard = Wizard.fromJson(this.data);
      this.refWizard = Wizard.fromJson(this.wizard);
    }
  },
  methods: {
    preview() {
      this.section = new Section();
      this.section.component = SectionWizard;
      this.section.data = this.wizard;
      this.section.helper = true;
      this.dialogOpen = true;
    },
    setLabel(value: string) {
      this.wizard.label = value;
      this.setUpdated();
    },
    setModule(item: Item) {
      this.wizard.module = item.key;
      this.setUpdated();
    },
    setPage(id: number, index: number) {
      if (id > 0) {
        const page = storeWizardItem.getWizardItem(id);
        const clone = [...this.wizard.wizardItems];
        clone[index] = page;
        this.wizard.wizardItems = clone;
      }
      this.setUpdated();
    },
    getCi98(): boolean {
      return (this.wizard.profil & EnumProfile.ci98) > 0;
    },
    setCi98(value: boolean) {
      if (value) {
        this.wizard.profil += EnumProfile.ci98
      } else {
        this.wizard.profil -= EnumProfile.ci98
      }
      this.setUpdated();
    },
    getActive(): boolean {
      return this.wizard.actif;
    },
    setActive(value: boolean) {
      this.wizard.actif = value;
      this.setUpdated();
    },
    getModuleValue(): string {
      const item = this.modules.find(a => a.key == this.wizard.module);
      if (item) {
        return item.title;
      } else {
        return "";
      }
    },
    addWizardItem() {
      const wizardItem = storeWizardItem.createNewWizardItem();
      wizardItem.wizardId = this.wizard.id;
      this.wizard.wizardItems.push(wizardItem);
      this.section = new Section();
      this.section.component = SectionAdminWizardItem;
      this.section.data = wizardItem;
      this.section.type = 'wizardItem';
      this.dialogOpen = true;
    },
    updateWizardItem(item: WizardItem) {
      this.section = new Section();
      this.section.component = SectionAdminWizardItem;
      this.section.data = item;
      this.section.type = 'wizardItem';
      this.dialogOpen = true;
    },
    deleteWizardItem(index: number) {
      this.wizard.wizardItems.splice(index, 1);
      this.setUpdated();
    },
    moveUp(index: number): void {
      const toIndex = index - 1;
      const currentItem = this.wizardItems[index];
      const temp = this.wizardItems[toIndex];
      temp.position = index;
      currentItem.position = toIndex;
      this.setUpdated();
    },
    moveDown(index: number): void {
      const toIndex = index + 1;
      const currentItem = this.wizardItems[index];
      const temp = this.wizardItems[toIndex];
      temp.position = index;
      currentItem.position = toIndex;
      this.setUpdated();
    },
    canMoveUp(index: number): boolean {
      if (index == 0) {
        return false;
      } else {
        return true;
      }
    },
    canMoveDown(index: number): boolean {
      if (this.wizard.wizardItems.length - 1 == index) {
        return false;
      } else {
        return true;
      }
    },
    save(): void {
      this.saveWizardItem().then(() => {
        if (this.wizard.id > 0) {
          this.wizard.update().then((res) => {
            this.wizard = res;
            this.refWizard = Wizard.fromJson(res);
            this.setUpdated();
          });
        } else {
          this.wizard.create().then((res) => {
            this.wizard = res;
            this.refWizard = Wizard.fromJson(res);
            this.setUpdated();
          });;
        }
      });
    },
    saveWizardItem(): Promise<boolean | void> {
      const promises = this.wizardItems.map((wizardItem: WizardItem) => {
        if (wizardItem.id > 0) {
          storeWizardItem.updateWizardItem(wizardItem);
        } else {
          storeWizardItem.createWizardItem(wizardItem);
        }
      });
      return Promise.all(promises).then(() => {
        // Wait for task to complete
      });
    },
    setUpdated() {
      const ref = JSON.stringify(this.refWizard);
      const wizard = JSON.stringify(this.wizard);
      this.updated = (ref != wizard);
    },
    closePreview(): void {
      this.dialogOpen = false;
    },
    closeDialog(): void {
      this.$emit('closeDialog');
    },
  },
});
</script>